import React, { useState } from 'react';
import { Box, Popover, useTheme } from '@mui/material';
import { Pill } from './Pill';


type PillColor = 'green' | 'blue' | 'violet' | 'gray';

const COLOR_SEQUENCE: PillColor[] = ['green', 'blue', 'violet'];

interface PillListProps {
  labels: string[];
  maxVisible?: number;
}

const PillList: React.FC<PillListProps> = ({ labels, maxVisible = 3 }) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const handleMoreClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (event: { stopPropagation: () => void; }) => {
    if (event) {
      event.stopPropagation();
    }

    setAnchorEl(null);
  };

  const getRandomPillColor = (index: number) => {
    const color = COLOR_SEQUENCE[index % COLOR_SEQUENCE.length];

    switch (color) {
      case 'blue':
      case 'violet':
        return theme.palette.custom[color];
      default:
        return theme.palette.success;
    }
  };

  const visiblePills = labels.slice(0, maxVisible);
  const remainingCount = labels.length - maxVisible;
  const hasMorePills = remainingCount > 0;

  return (
    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
      {visiblePills.map((label, index) => (
        <Pill
          key={`visible-${index}`}
          text={label}
          pallet={getRandomPillColor(index)}
        />
      ))}

      {hasMorePills && (
        <Box
          onClick={handleMoreClick}
          sx={{
            cursor: 'pointer',
            '&:hover': { opacity: 0.9 },
          }}
        >
          <Pill
            text={`+${remainingCount}`}
            pallet={theme.palette.custom['gray']}
          />
        </Box>
      )}

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        slotProps={{
          paper: {
            sx: {
              p: 2,
              maxWidth: 320,
              boxShadow: theme => theme.shadows[2],
            },
          },
        }}
      >
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          {
            labels.map((label, index) => (
              <Pill
                key={`all-${index}`}
                text={label}
                pallet={getRandomPillColor(index)}
              />
            ))
          }
        </Box>
      </Popover>
    </Box>
  );
};

export default PillList;
